import { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Lock, Mail, ArrowRight, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Admin Login | Four Bees Consultancy";
  }, []);

  if (user && user.email) return <Navigate to="/admin" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Signed in.");
      navigate("/admin", { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message =
        formatApiErrorDetail(detail) ||
        (err.response?.status === 401
          ? "Invalid email or password."
          : !err.response
          ? "Couldn't reach the server. Check your connection and try again."
          : "Login failed. Please try again.");
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-cream flex flex-col items-center justify-center px-6"
      data-testid="admin-login-page"
    >
      <a
        href="/"
        className="absolute top-6 left-6 flex items-center gap-2 text-charcoal/60 hover:text-charcoal transition-colors text-sm"
        data-testid="login-back-link"
      >
        <ArrowLeft size={16} />
        Back to website
      </a>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-gold rounded-sm mb-5">
            <Lock className="w-6 h-6 text-charcoal" strokeWidth={2} />
          </div>
          <h1 className="font-heading text-3xl font-semibold text-charcoal tracking-tight mb-2">
            Admin sign in
          </h1>
          <p className="text-sm text-charcoal/60">
            Four Bees Consultancy — admin panel access
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white border border-border-gray rounded-sm p-7 md:p-8 space-y-5"
          data-testid="admin-login-form"
        >
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-charcoal/70 mb-2">
              Email
            </label>
            <div className="relative">
              <Mail
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40"
              />
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="contact-input pl-10"
                placeholder="simon@fourbeesconsultancy.co.uk"
                data-testid="login-email-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-charcoal/70 mb-2">
              Password
            </label>
            <div className="relative">
              <Lock
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal/40"
              />
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="contact-input pl-10"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="login-submit-button"
          >
            {submitting ? "Signing in..." : "Sign in"}
            {!submitting && <ArrowRight size={16} strokeWidth={2.5} />}
          </button>
        </form>
      </div>

      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#FDFBF7",
            border: "1px solid #D4D4D0",
            color: "#1A1A1A",
            fontFamily: "Manrope, sans-serif",
          },
        }}
      />
    </div>
  );
}
